import { getFullName } from "utils/Haim/utils";
import type { CrmContactRow } from "../../../@types/webotServer";

/**
 * The name to show for a mirror row — ONE rule, because the row carries the same name
 * twice and only one of the two is always there.
 *
 * ── The bug this exists for (2026-09-15) ──────────────────────────────────────────
 *
 * `row.name` is `contact_full_name`, a generated column over `doc->>'contactFullName'` —
 * and `contactFullName` is **stamped by a Cloud Function**
 * (`addContactRealtorAndOrgFields`), in a SECOND write a few seconds after the document is
 * created. `row.firstName` / `row.lastName` are `callerFirstName` / `callerLastName`, which
 * the create itself writes.
 *
 * So for the first few seconds of a contact's life the mirror holds the name under
 * `caller_first_name` and NOTHING under `contact_full_name`. Now that a create lands in the
 * list immediately (the endpoint drains its own key inline rather than waiting for the
 * once-a-minute cron), that window is on screen: a realtor pressed "לקוח חדש", typed
 * "בדיקה 222", and watched the row appear reading **ללא שם**. It self-heals on the next
 * drain, but "the contact I just created has no name" reads as data loss, and the list does
 * not refetch on its own to undo the impression.
 *
 * The side panel never had the bug because it already fell back to `callerFirstName`
 * (`CrmContactPanel`), which is exactly why the two disagreed on one screen — the card
 * beside the list showed the name the row claimed not to have.
 *
 * ── Why the fallback and not a server-side derivation ─────────────────────────────
 *
 * Deriving `contactFullName` on the write path is explicitly forbidden
 * (`contactWriteRules.ts`): it races the Cloud Function that owns the field, and a second
 * copy of the derivation is one that drifts. This is a READ-side fallback over data the row
 * already carries, so it needs no new field, no new write, and no timing assumption — it is
 * simply correct whenever the stamp has not landed yet, including for the historical rows
 * that never got one.
 *
 * It reproduces `getUserFullName` exactly (`getFullName(callerFirstName, callerLastName)`),
 * so the fallback and the stamp can never disagree about what the name IS.
 *
 * @returns the display name, or `""` when the contact genuinely has none — callers render
 *   their own placeholder ("ללא שם") so a nameless contact still reads as nameless.
 */
export const crmRowDisplayName = (row: Pick<CrmContactRow, "name" | "firstName" | "lastName">): string =>
  row.name?.trim() || getFullName(row.firstName ?? undefined, row.lastName ?? undefined);
